import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  Link,
} from '@mui/material'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useNavigate } from 'react-router-dom'
import { authApi } from '../api/auth'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'

const createForgotPasswordSchema = (t: (key: string) => string) => z.object({
  email: z.string().email(t('auth.email') + ' inválido'),
})

type ForgotPasswordFormData = z.infer<ReturnType<typeof createForgotPasswordSchema>>

const ForgotPasswordPage: React.FC = () => {
  const { t } = useTranslation()
  const forgotPasswordSchema = React.useMemo(() => createForgotPasswordSchema(t), [t])
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const navigate = useNavigate()

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordSchema),
  })

  const onSubmit = async (data: ForgotPasswordFormData) => {
    setError(null)
    setSuccess(false)
    setIsLoading(true)
    
    try {
      await authApi.forgotPassword(data.email)
      setSuccess(true)
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Erro ao solicitar recuperação de senha')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Container maxWidth="sm" sx={{ mt: 8 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Box sx={{ mb: 2 }}>
          <Link
            component="button"
            variant="body2"
            onClick={() => navigate('/login')}
            sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}
          >
            <ArrowBackIcon fontSize="small" />
            Voltar para login
          </Link>
        </Box>

        <Typography variant="h4" component="h1" gutterBottom align="center">
          Esqueci minha senha
        </Typography>

        <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
          Digite seu email e enviaremos um link para redefinir sua senha.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Se o email existir em nosso sistema, você receberá um link para redefinir sua senha.
            Verifique sua caixa de entrada e spam.
          </Alert>
        )}

        <form onSubmit={handleSubmit(onSubmit)}>
          <TextField
            {...register('email')}
            label={t('auth.email')}
            type="email"
            fullWidth
            margin="normal"
            error={!!errors.email}
            helperText={errors.email?.message}
            disabled={isLoading || success}
            autoFocus
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            sx={{ mt: 3, mb: 2 }}
            disabled={isLoading || success}
          >
            {isLoading ? 'Enviando...' : 'Enviar link de recuperação'}
          </Button>
        </form>
      </Paper>
    </Container>
  )
}

export default ForgotPasswordPage





